import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Copy, RefreshCw, Store, MapPin, Phone, Mail, Crown, GitMerge, Loader2, ShieldCheck,
  AlertTriangle, CheckCircle2, Search, Filter, Building2, Star, Inbox, Eye, ArrowRight, Link2, Wand2,
  History, Undo2, User, Clock, Archive,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BRAND = "#EE722E";

const CONF_META = {
  haute: { label: "Confiance haute", cls: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" },
  moyenne: { label: "À vérifier", cls: "bg-[rgba(233,184,0,0.18)] text-[#5A4200]" },
};

// Carte d'un membre du groupe (spot candidat), avec choix de la référence.
function MemberCard({ m, isCanonical, isIncluded, onPickCanonical, onToggleInclude }) {
  return (
    <div
      className={`relative rounded-xl border p-3 transition-[border-color,background-color] ${
        isCanonical
          ? "border-[var(--shariti-orange)] bg-[#FFF7F1]"
          : isIncluded
          ? "border-[var(--border-soft)] bg-white"
          : "border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] opacity-70"
      }`}
      data-testid={`dup-member-${m.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Store className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
            <span className="break-words text-sm font-semibold text-ink">{m.nom_donne || m.normalized_name || m.id}</span>
          </div>
          <div className="mt-0.5 font-mono text-[11px] text-muted-ink">{m.id}</div>
        </div>
        {isCanonical ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`dup-canon-badge-${m.id}`}>
            <Crown className="h-3 w-3" /> Référence
          </span>
        ) : (
          <label className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-muted-ink">
            <input type="checkbox" checked={isIncluded} onChange={onToggleInclude} data-testid={`dup-include-${m.id}`} />
            Fusionner
          </label>
        )}
      </div>

      <div className="mt-2 space-y-1 text-[11px] text-muted-ink">
        <div className="flex items-start gap-1.5"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-words">{m.address_full || [m.rue, m.ville].filter(Boolean).join(", ") || "—"}</span></div>
        <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 shrink-0" />{m.telephone || "—"}</div>
        <div className="flex items-start gap-1.5"><Mail className="mt-0.5 h-3 w-3 shrink-0" /><span className="break-all">{m.email || "—"}</span></div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[10px] font-semibold text-ink" title="Nombre d'exploitations (bookings) rattachées">
          {m.exploitations} exploitation{m.exploitations > 1 ? "s" : ""}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.has_gps ? "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" : "bg-[rgba(214,69,69,0.12)] text-[#9B2226]"}`}>
          {m.has_gps ? "GPS ✓" : "GPS manquant"}
        </span>
        {m.status && <span className="rounded-full bg-[rgba(47,111,237,0.10)] px-2 py-0.5 text-[10px] font-semibold text-[#2F6FED]">{m.status}</span>}
      </div>

      {!isCanonical && (
        <Button variant="ghost" size="sm" onClick={onPickCanonical}
          className="mt-2 h-7 w-full text-[11px] text-[var(--shariti-orange)] hover:bg-[#FFF3E9]"
          data-testid={`dup-set-canon-${m.id}`}>
          <Crown className="mr-1 h-3 w-3" /> Choisir comme référence
        </Button>
      )}
    </div>
  );
}

function ClusterCard({ cluster, onMerged }) {
  const [canonicalId, setCanonicalId] = useState(cluster.suggested_canonical_id);
  const [included, setIncluded] = useState(
    () => new Set(cluster.members.filter((m) => m.id !== cluster.suggested_canonical_id).map((m) => m.id))
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const conf = CONF_META[cluster.confidence] || CONF_META.moyenne;
  const canonical = cluster.members.find((m) => m.id === canonicalId);
  const dupIds = useMemo(() => [...included].filter((id) => id !== canonicalId), [included, canonicalId]);

  const pickCanonical = (id) => {
    setCanonicalId(id);
    setIncluded((prev) => {
      const next = new Set(prev);
      next.delete(id);
      cluster.members.forEach((m) => { if (m.id !== id) next.add(m.id); });
      return next;
    });
  };
  const toggleInclude = (id) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const doPreview = async () => {
    setPreviewBusy(true);
    setPreview(null);
    setPreviewOpen(true);
    try {
      const { data } = await api.post("/spots/merge/preview", { canonical_id: canonicalId, duplicate_ids: dupIds });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu impossible");
      setPreviewOpen(false);
    } finally { setPreviewBusy(false); }
  };

  const doMerge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/spots/merge", { canonical_id: canonicalId, duplicate_ids: dupIds });
      toast.success(`Fusion effectuée — ${data.merged.length} doublon(s) rattaché(s) à ${canonicalId}`);
      setConfirmOpen(false);
      onMerged(cluster.cluster_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fusion impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`dup-cluster-${cluster.cluster_id}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${conf.cls}`}>
          {cluster.confidence === "haute" ? <ShieldCheck className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          {conf.label}
        </span>
        <span className="text-[11px] text-muted-ink">Rapproché par : <b className="text-ink">{cluster.signals.join(", ")}</b></span>
        <span className="ml-auto text-[11px] font-semibold text-muted-ink">{cluster.size} spots</span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cluster.members.map((m) => (
          <MemberCard key={m.id} m={m}
            isCanonical={m.id === canonicalId}
            isIncluded={included.has(m.id)}
            onPickCanonical={() => pickCanonical(m.id)}
            onToggleInclude={() => toggleInclude(m.id)} />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border-soft)] pt-3">
        <p className="text-[11px] text-muted-ink">
          <Crown className="mr-1 inline h-3 w-3 text-[var(--shariti-orange)]" />
          La <b>référence</b> ({canonicalId}) est conservée intacte. Les champs vides seront complétés depuis les doublons — jamais écrasés. Utilisez <b>Aperçu</b> pour simuler sans risque.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={doPreview} disabled={dupIds.length === 0}
            className="border-[var(--shariti-orange)] bg-white text-[var(--shariti-orange)] hover:bg-[#FFF3E9]"
            data-testid={`dup-preview-btn-${cluster.cluster_id}`}>
            <Eye className="mr-1.5 h-4 w-4" /> Aperçu
          </Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={dupIds.length === 0}
            className="text-white" style={{ background: BRAND }} data-testid={`dup-merge-btn-${cluster.cluster_id}`}>
            <GitMerge className="mr-1.5 h-4 w-4" /> Fusionner ({dupIds.length})
          </Button>
        </div>
      </div>

      <AlertDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <AlertDialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="dup-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[var(--shariti-orange)]" /> Aperçu de la fusion (simulation)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2 rounded-lg border border-[rgba(47,111,237,0.25)] bg-[rgba(47,111,237,0.06)] p-2.5 text-[12px] text-[#1F4E9B]">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  <span><b>Aucune modification n'est effectuée.</b> Ceci est une simulation : elle montre exactement ce que produirait la fusion.</span>
                </div>

                {previewBusy && (
                  <div className="flex items-center justify-center gap-2 py-6 text-muted-ink" data-testid="dup-preview-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Calcul de l'aperçu…
                  </div>
                )}

                {!previewBusy && preview && (
                  <div className="space-y-3" data-testid="dup-preview-body">
                    {/* Référence conservée */}
                    <div className="rounded-lg border border-[var(--shariti-orange)] bg-[#FFF7F1] p-2.5">
                      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">
                        <Crown className="h-3.5 w-3.5" /> Référence conservée
                      </div>
                      <div className="mt-1 font-semibold text-ink">{preview.canonical?.nom}</div>
                      <div className="font-mono text-[11px] text-muted-ink">{preview.canonical?.id}{preview.canonical?.address_full ? ` · ${preview.canonical.address_full}` : ""}</div>
                    </div>

                    {/* Doublons archivés */}
                    <div>
                      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                        {preview.duplicates?.length || 0} doublon(s) → archivé(s) (tombstone)
                      </div>
                      <div className="space-y-1">
                        {(preview.duplicates || []).map((d) => (
                          <div key={d.id} className="flex items-center gap-2 rounded-md bg-[var(--surface-2)] px-2 py-1.5 text-[12px]">
                            <ArrowRight className="h-3 w-3 shrink-0 text-muted-ink" />
                            <span className="min-w-0 flex-1 break-words text-ink">{d.nom} <span className="font-mono text-[10px] text-muted-ink">({d.id})</span></span>
                            <span className="shrink-0 text-[10px] text-muted-ink">{d.references ?? 0} réf.</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Champs complétés */}
                    <div>
                      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Champs complétés sur la référence</div>
                      {(preview.fields_to_fill?.length || 0) + (preview.contacts_to_add?.length || 0) === 0 ? (
                        <p className="text-[12px] text-muted-ink">Aucun — la référence est déjà complète (rien ne sera écrasé).</p>
                      ) : (
                        <div className="space-y-1">
                          {(preview.fields_to_fill || []).map((f, i) => (
                            <div key={`f${i}`} className="flex items-center gap-2 text-[12px]">
                              <CheckCircle2 className="h-3 w-3 shrink-0 text-[#1F9D55]" />
                              <span className="font-semibold text-ink">{f.field}</span>
                              <span className="truncate text-muted-ink">= {String(f.value)}</span>
                            </div>
                          ))}
                          {(preview.contacts_to_add || []).map((c, i) => (
                            <div key={`c${i}`} className="flex items-center gap-2 text-[12px]">
                              <CheckCircle2 className="h-3 w-3 shrink-0 text-[#1F9D55]" />
                              <span className="font-semibold text-ink">contact secondaire</span>
                              <span className="break-words text-muted-ink">{[c.nom, c.telephone, c.email].filter(Boolean).join(" · ") || "—"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Références rebranchées */}
                    <div>
                      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                        <Link2 className="h-3.5 w-3.5" /> Données rebranchées vers la référence
                      </div>
                      {preview.references_total === 0 ? (
                        <p className="text-[12px] text-muted-ink">Aucune donnée rattachée aux doublons.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(preview.references_to_relink || {}).map(([coll, n]) => (
                            <span key={coll} className="rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[11px] font-semibold text-ink">
                              {coll} : {n}
                            </span>
                          ))}
                          <span className="rounded-full bg-[rgba(31,157,85,0.14)] px-2 py-0.5 text-[11px] font-bold text-[#0F5A2F]">
                            Total : {preview.references_total}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[var(--border-soft)] bg-white" data-testid="dup-preview-close">
              Fermer
            </Button>
            <Button onClick={() => { setPreviewOpen(false); setConfirmOpen(true); }} disabled={previewBusy || !preview}
              className="text-white" style={{ background: BRAND }} data-testid="dup-preview-proceed">
              <GitMerge className="mr-1.5 h-4 w-4" /> Procéder à la fusion
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="dup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la fusion ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <b>{dupIds.length}</b> doublon(s) seront rattachés à la référence{" "}
                  <b>{canonical?.nom_donne || canonical?.normalized_name || canonicalId}</b> ({canonicalId}).
                </p>
                <ul className="list-inside list-disc text-[12px] text-muted-ink">
                  <li>La référence n'est <b>jamais écrasée</b> ; seuls ses champs vides sont complétés.</li>
                  <li>Toutes les données rattachées (planning, appels, pointages…) sont rebranchées.</li>
                  <li>Les doublons passent en archive (tombstone) — <b>aucune suppression</b>.</li>
                </ul>
                <div className="rounded-lg bg-[var(--surface-2)] p-2 font-mono text-[11px] text-muted-ink">
                  {dupIds.join(", ")} → {canonicalId}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-[var(--border-soft)] bg-white" data-testid="dup-confirm-cancel">Annuler</Button>
            <Button onClick={doMerge} disabled={busy} className="text-white" style={{ background: BRAND }} data-testid="dup-confirm-ok">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1.5 h-4 w-4" /> Confirmer la fusion</>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function DoublonsSpots() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conf, setConf] = useState("tous");
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [merged, setMerged] = useState(() => new Set());
  const [massOpen, setMassOpen] = useState(false);   // dialogue « Tout fusionner »
  const [massBusy, setMassBusy] = useState(false);
  const [view, setView] = useState("groupes");       // "groupes" | "journal"
  const [journal, setJournal] = useState(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [undoCtx, setUndoCtx] = useState(null);      // fusion à annuler (confirmation)
  const [undoBusy, setUndoBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/spots/duplicates");
      setData(data);
      setMerged(new Set());
    } catch { toast.error("Détection impossible"); setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Journal des fusions : historique (qui a fusionné quoi et quand) + annulation.
  const loadJournal = useCallback(async () => {
    setJournalLoading(true);
    try {
      const { data } = await api.get("/spots/merges", { params: { limit: 200 } });
      setJournal(data.merges || []);
    } catch { toast.error("Chargement du journal impossible"); setJournal([]); }
    finally { setJournalLoading(false); }
  }, []);
  useEffect(() => { if (view === "journal" && journal === null) loadJournal(); }, [view, journal, loadJournal]);

  const doUndo = async () => {
    if (!undoCtx) return;
    setUndoBusy(true);
    try {
      const { data } = await api.post(`/spots/merges/${undoCtx.id}/undo`);
      toast.success(`Fusion annulée — ${data.restored?.length ?? 0} spot(s) rétabli(s), ${data.references_rebranchees ?? 0} référence(s) rebranchée(s)`);
      setUndoCtx(null);
      await loadJournal();
      setData(null); setMerged(new Set()); load();   // le doublon réapparaît → on réanalyse
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Annulation impossible");
    } finally { setUndoBusy(false); }
  };

  const clusters = useMemo(() => {
    let cs = (data?.clusters || []).filter((c) => !merged.has(c.cluster_id));
    if (conf !== "tous") cs = cs.filter((c) => c.confidence === conf);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      cs = cs.filter((c) => c.members.some((m) =>
        [m.normalized_name, m.nom_donne, m.ville, m.email, m.id, m.address_full]
          .some((x) => (x || "").toLowerCase().includes(s))));
    }
    return cs;
  }, [data, conf, q, merged]);

  const counts = useMemo(() => {
    const all = (data?.clusters || []).filter((c) => !merged.has(c.cluster_id));
    return {
      tous: all.length,
      haute: all.filter((c) => c.confidence === "haute").length,
      moyenne: all.filter((c) => c.confidence === "moyenne").length,
      spots: all.reduce((n, c) => n + c.size, 0),
    };
  }, [data, merged]);

  // Spots concernés UNIQUEMENT par les groupes « haute confiance » (pour l'aperçu
  // du dialogue de fusion en masse : nb de doublons rattachés = spots - groupes).
  const hauteSpots = useMemo(() => {
    const all = (data?.clusters || []).filter((c) => !merged.has(c.cluster_id) && c.confidence === "haute");
    return all.reduce((n, c) => n + c.size, 0);
  }, [data, merged]);

  const doMergeAll = async () => {
    setMassBusy(true);
    try {
      const { data: res } = await api.post("/spots/merge-all", { confidence: "haute" });
      const nbErr = (res.errors || []).length;
      toast.success(
        `Fusion en masse terminée — ${res.clusters_merged} groupe(s) fusionné(s), `
        + `${res.spots_merged} doublon(s) rattaché(s)`
        + (nbErr ? ` (${nbErr} erreur(s))` : "")
      );
      setMassOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fusion en masse impossible");
    } finally { setMassBusy(false); }
  };

  return (
    <>
      <PageHeader
        title="Doublons de spots"
        subtitle="Rapprochez et fusionnez les fiches en double. La référence (spot le plus ancien/riche) est conservée ; ses champs ne sont jamais écrasés."
        action={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button onClick={() => setMassOpen(true)} disabled={loading || counts.haute === 0}
                className="text-white disabled:opacity-50" style={{ background: BRAND }} data-testid="dup-merge-all-btn"
                title="Fusionner automatiquement tous les groupes « haute confiance »">
                <Wand2 className="mr-1.5 h-4 w-4" /> Tout fusionner ({counts.haute})
              </Button>
            )}
            <Button variant="outline" onClick={load} disabled={loading} className="border-[var(--border-soft)] bg-white" data-testid="dup-refresh">
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Réanalyser
            </Button>
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-xs flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (magasin, ville, email…)" className="bg-white pl-9" data-testid="dup-search" />
          </div>
          <Select value={conf} onValueChange={setConf}>
            <SelectTrigger className="h-10 w-[190px] bg-white" data-testid="dup-filter-conf">
              <Filter className="mr-1.5 h-4 w-4 text-muted-ink" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Toutes ({counts.tous})</SelectItem>
              <SelectItem value="haute">Confiance haute ({counts.haute})</SelectItem>
              <SelectItem value="moyenne">À vérifier ({counts.moyenne})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      <PageBody>
        {/* Onglets : groupes détectés vs journal des fusions */}
        <div className="mb-4 inline-flex rounded-xl border border-[var(--border-soft)] bg-white p-1" data-testid="dup-tabs">
          <button onClick={() => setView("groupes")} data-testid="dup-tab-groupes"
            className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition-colors ${view === "groupes" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:text-ink"}`}>
            <Copy className="mr-1.5 inline h-3.5 w-3.5" /> Groupes détectés
          </button>
          <button onClick={() => setView("journal")} data-testid="dup-tab-journal"
            className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition-colors ${view === "journal" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:text-ink"}`}>
            <History className="mr-1.5 inline h-3.5 w-3.5" /> Journal des fusions
          </button>
        </div>

        {view === "groupes" && (
          <>
            {!loading && data && (
              <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-[rgba(47,111,237,0.25)] bg-[rgba(47,111,237,0.06)] px-3 py-2.5 text-[12px] text-[#1F4E9B]" data-testid="dup-summary">
                <Copy className="h-4 w-4 shrink-0" />
                <span><b>{counts.tous}</b> groupe(s) de doublons détecté(s) — <b>{counts.spots}</b> spots concernés. Les groupes « Confiance haute » (Place ID, adresse) sont les plus sûrs.</span>
              </div>
            )}

            {loading ? (
              <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>
            ) : clusters.length === 0 ? (
              <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="dup-empty">
                <CheckCircle2 className="h-10 w-10 text-[#1F9D55]" />
                <h3 className="mt-3 text-base font-bold text-ink">Aucun doublon à traiter</h3>
                <p className="mt-1 max-w-sm text-sm text-muted-ink">Aucun groupe ne correspond à ces filtres. La banque de spots est propre de ce côté.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {clusters.map((c) => <ClusterCard key={c.cluster_id} cluster={c} onMerged={(id) => setMerged((prev) => new Set(prev).add(id))} />)}
              </div>
            )}
          </>
        )}

        {view === "journal" && (
          <MergeJournal
            journal={journal}
            loading={journalLoading}
            onRefresh={loadJournal}
            onAskUndo={(m) => setUndoCtx(m)}
          />
        )}
      </PageBody>

      {/* Fusion en masse (haute confiance) — confirmation */}
      <AlertDialog open={massOpen} onOpenChange={(o) => { if (!massBusy) setMassOpen(o); }}>
        <AlertDialogContent data-testid="dup-merge-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-[var(--shariti-orange)]" /> Tout fusionner (haute confiance)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  <b>{counts.haute}</b> groupe(s) « <b>Confiance haute</b> » seront fusionnés automatiquement,
                  soit <b>{Math.max(hauteSpots - counts.haute, 0)}</b> doublon(s) rattaché(s) à leur référence.
                </p>
                <div className="flex items-start gap-2 rounded-lg border border-[rgba(31,157,85,0.28)] bg-[rgba(31,157,85,0.08)] p-2.5 text-[12px] text-[#0F5A2F]">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Seuls les groupes rapprochés par des signaux <b>fiables</b> (Google Place ID, adresse + enseigne, GPS + enseigne) sont traités. Les groupes « à vérifier » restent en validation manuelle.</span>
                </div>
                <ul className="list-inside list-disc text-[12px] text-muted-ink">
                  <li>Pour chaque groupe, la <b>référence</b> (spot le plus ancien/riche) est conservée et <b>jamais écrasée</b>.</li>
                  <li>Toutes les données rattachées (planning, appels, pointages…) sont rebranchées vers la référence.</li>
                  <li>Les doublons passent en archive (tombstone) — <b>aucune suppression</b>.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setMassOpen(false)} disabled={massBusy}
              className="border-[var(--border-soft)] bg-white" data-testid="dup-merge-all-cancel">Annuler</Button>
            <Button onClick={doMergeAll} disabled={massBusy || counts.haute === 0}
              className="text-white" style={{ background: BRAND }} data-testid="dup-merge-all-confirm">
              {massBusy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Fusion en cours…</> : <><Wand2 className="mr-1.5 h-4 w-4" /> Fusionner {counts.haute} groupe(s)</>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Annuler une fusion — fenêtre de sécurité (confirmation) */}
      <AlertDialog open={!!undoCtx} onOpenChange={(o) => { if (!o && !undoBusy) setUndoCtx(null); }}>
        <AlertDialogContent data-testid="dup-undo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-[var(--shariti-orange)]" /> Annuler cette fusion ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Le(s) doublon(s) archivé(s) seront <b>rétablis</b> et redeviendront des fiches actives,
                  et leurs données rattachées (planning, appels, pointages…) seront <b>rebranchées</b> vers eux.
                </p>
                {undoCtx && (
                  <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-[12px]">
                    <div><b>Référence :</b> {undoCtx.canonical_nom}</div>
                    <div><b>Doublon(s) à rétablir :</b> {(undoCtx.duplicates || []).map((d) => d.nom).join(", ") || (undoCtx.duplicate_ids || []).join(", ")}</div>
                    <div className="text-muted-ink">Fusion du {frDateTime(undoCtx.created_at)} par {undoCtx.actor_name}</div>
                  </div>
                )}
                <div className="flex items-start gap-2 rounded-lg border border-[rgba(233,184,0,0.4)] bg-[rgba(233,184,0,0.12)] p-2.5 text-[12px] text-[#5A4200]">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Les champs de la référence complétés lors de cette fusion seront remis dans leur état d'origine. Aucune donnée n'est supprimée.</span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setUndoCtx(null)} disabled={undoBusy}
              className="border-[var(--border-soft)] bg-white" data-testid="dup-undo-cancel">Retour</Button>
            <Button onClick={doUndo} disabled={undoBusy}
              className="text-white" style={{ background: BRAND }} data-testid="dup-undo-confirm">
              {undoBusy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Annulation…</> : <><Undo2 className="mr-1.5 h-4 w-4" /> Oui, annuler la fusion</>}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Format date/heure court FR pour le journal.
function frDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Journal des fusions : liste des fusions (qui/quoi/quand) + bouton Annuler.
function MergeJournal({ journal, loading, onRefresh, onAskUndo }) {
  if (loading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  }
  const rows = journal || [];
  if (rows.length === 0) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="journal-empty">
        <History className="h-10 w-10 text-muted-ink" />
        <h3 className="mt-3 text-base font-bold text-ink">Aucune fusion enregistrée</h3>
        <p className="mt-1 max-w-sm text-sm text-muted-ink">Les fusions réalisées apparaîtront ici, avec la possibilité d'annuler la plus récente.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="journal-list">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-muted-ink">{rows.length} fusion(s) enregistrée(s) — la plus récente d'abord.</div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="border-[var(--border-soft)] bg-white" data-testid="journal-refresh">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualiser
        </Button>
      </div>
      {rows.map((m) => (
        <div key={m.id} className={`rounded-2xl border bg-white p-4 ${m.undone ? "border-[var(--border-soft)] opacity-70" : "border-[var(--border-soft)]"}`} data-testid={`journal-row-${m.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[11px] font-bold text-[#8A3A10]">
                  <Crown className="h-3 w-3" /> {m.canonical_nom}
                </span>
                {m.undone
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[10px] font-bold uppercase text-muted-ink"><Undo2 className="h-3 w-3" /> Annulée</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-bold uppercase text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" /> Fusionnée</span>}
                {m.source && m.source !== "manual" && <span className="rounded-full bg-[rgba(47,111,237,0.10)] px-2 py-0.5 text-[10px] font-semibold text-[#1E4FB8]">{m.source === "merge_all" ? "en masse" : m.source}</span>}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-ink">
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {frDateTime(m.created_at)}</span>
                <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {m.actor_name || "—"}</span>
                <span className="inline-flex items-center gap-1"><Archive className="h-3.5 w-3.5" /> {(m.duplicate_ids || []).length} doublon(s) archivé(s)</span>
                {m.references_repointees > 0 && <span className="inline-flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> {m.references_repointees} donnée(s) rebranchée(s)</span>}
              </div>
              <div className="mt-1 text-[12px] text-ink">
                Doublon(s) : <b>{(m.duplicates || []).map((d) => d.nom).join(", ") || (m.duplicate_ids || []).join(", ")}</b>
              </div>
              {(m.fields_filled || []).length > 0 && (
                <div className="mt-1 text-[11px] text-muted-ink">
                  Complété : {(m.fields_filled || []).map((f) => f.field).join(", ")}
                </div>
              )}
              {m.undone && <div className="mt-1 text-[11px] italic text-muted-ink">Annulée le {frDateTime(m.undone_at)}{m.undone_by ? ` par ${m.undone_by}` : ""}.</div>}
            </div>
            <div className="shrink-0">
              {m.can_undo ? (
                <Button variant="outline" size="sm" onClick={() => onAskUndo(m)}
                  className="border-[rgba(238,114,46,0.5)] bg-white text-[#8A3A10] hover:bg-[#FFF3E9]"
                  data-testid={`journal-undo-${m.id}`}>
                  <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Annuler
                </Button>
              ) : !m.undone ? (
                <span className="text-[10px] text-muted-ink" title="Annulez d'abord la fusion plus récente de cette référence">Non annulable</span>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
